/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useState, useEffect, useCallback, useRef } from "react";
import { InteractiveAuth } from "matrix-js-sdk";
import {
  createClient,
  type MatrixClient,
  type RegisterResponse,
} from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";

import { initClient } from "../utils/matrix";
import { type Session } from "../ClientContext";
import { Config } from "../config/Config";
import {
  getHomeserverAssociatedDomain,
  isKnownHomeserver,
} from "../knownHomeserversMap";
import { widget } from "../widget";
import { callTraceJournal, describeTraceError } from "../telemetry";

function isGuestSessionResponse(
  value: Record<string, unknown>,
): value is Record<"user_id" | "access_token" | "device_id", string> {
  return (
    typeof value.user_id === "string" &&
    typeof value.access_token === "string" &&
    typeof value.device_id === "string"
  );
}

export const useInteractiveRegistration = (
  oldClient?: MatrixClient,
): {
  privacyPolicyUrl?: string;
  recaptchaKey?: string;
  register: (
    username: string,
    password: string,
    displayName: string,
    recaptchaResponse: string,
    passwordlessUser: boolean,
  ) => Promise<[MatrixClient, Session]>;
} => {
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState<string | undefined>(
    undefined,
  );
  const [recaptchaKey, setRecaptchaKey] = useState<string | undefined>(
    undefined,
  );

  const authClient = useRef<MatrixClient | undefined>(undefined);
  if (!authClient.current) {
    authClient.current = createClient({
      baseUrl: Config.defaultHomeserverUrl()!,
    });
  }
  const homeserver = Config.defaultServerName() ?? "";
  const hasCustomGuestRegistration = isKnownHomeserver(homeserver);

  useEffect(() => {
    if (widget) return;
    setPrivacyPolicyUrl(undefined);
    setRecaptchaKey(undefined);
  }, []);

  const register = useCallback(
    async (
      username: string,
      password: string,
      displayName: string,
      recaptchaResponse: string,
      passwordlessUser: boolean,
    ): Promise<[MatrixClient, Session]> => {
      if (!hasCustomGuestRegistration) {
        throw new Error(
          `Гостевая регистрация не поддерживается для homeserver "${homeserver}"`,
        );
      }

      const interactiveAuth = new InteractiveAuth({
        matrixClient: authClient.current!,
        doRequest: async (auth): Promise<RegisterResponse> => {
          const backendDomain = getHomeserverAssociatedDomain(
            homeserver,
            "backend",
          );
          const response = await fetch(`https://${backendDomain}/guests/new`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ display_name: displayName }),
          });

          const responseText = await response.text();
          let responseData: Record<string, unknown> = {};

          if (responseText) {
            try {
              responseData = JSON.parse(responseText) as Record<
                string,
                unknown
              >;
            } catch {
              responseData = {};
            }
          }

          if (!response.ok) {
            const errorMessage =
              typeof responseData.error === "string"
                ? responseData.error
                : responseText || response.statusText;
            throw new Error(errorMessage);
          }

          if (!isGuestSessionResponse(responseData)) {
            throw new Error("Некорректный ответ сервера гостевой регистрации");
          }

          return responseData;
        },
        stateUpdated: (nextStage, status): void => {
          callTraceJournal.record({
            eventType: "auth.interactive_stage_changed",
            stage: "auth",
            severity: status.error ? "error" : "info",
            outcome: "state_changed",
            reasonCode: status.error ? "interactive_auth_error" : null,
            payload: { nextStage },
          });
          if (status.error) {
            throw new Error(status.error);
          }

          if (nextStage === "m.login.terms") {
            interactiveAuth
              .submitAuthDict({
                type: "m.login.terms",
              })
              .catch((e) => {
                logger.error(e);
              });
          } else if (nextStage === "m.login.recaptcha") {
            interactiveAuth
              .submitAuthDict({
                type: "m.login.recaptcha",
                response: recaptchaResponse,
              })
              .catch((e) => {
                logger.error(e);
              });
          }
        },
        requestEmailToken: async (): Promise<{ sid: string }> =>
          Promise.resolve({ sid: "dummy" }),
      });

      // XXX: This claims to return an IAuthData which contains none of these
      // things - the js-sdk types may be wrong?
      /* eslint-disable camelcase,@typescript-eslint/no-explicit-any */
      let authData: any;
      try {
        authData = await interactiveAuth.attemptAuth();
      } catch (error) {
        callTraceJournal.record({
          eventType: "auth.guest_registration_failed",
          stage: "auth",
          severity: "error",
          outcome: "failed",
          reasonCode: "interactive_registration_failed",
          payload: {
            operation: "interactive_registration",
            ...describeTraceError(error),
          },
        });
        throw error;
      }
      const { user_id, access_token, device_id } = authData;
      await oldClient?.logout(true);
      const client = await initClient(
        {
          baseUrl: Config.defaultHomeserverUrl()!,
          accessToken: access_token,
          userId: user_id,
          deviceId: device_id,
        },
        false,
      );

      await client.setDisplayName(displayName);

      const session: Session = {
        user_id,
        device_id,
        access_token,
        passwordlessUser,
      };
      /* eslint-enable camelcase */

      if (passwordlessUser) {
        session.tempPassword = password;
      }

      const user = client.getUser(client.getUserId()!)!;
      user.setRawDisplayName(displayName);
      user.setDisplayName(displayName);

      return [client, session];
    },
    [hasCustomGuestRegistration, homeserver, oldClient],
  );

  return { privacyPolicyUrl, recaptchaKey, register };
};
